// src/hooks/useIntersectionAnimation.js
import { useEffect, useRef } from "react";

/**
 * 通用视口进入动画 Hook（支持离开重置状态）
 * @param {number} delayGap - 元素之间的延迟时间（默认200ms）
 * @param {number} threshold - 触发阈值（默认0.1）
 * @param {boolean} resetOnLeave - 是否在离开视口时重置状态（默认true）
 * @returns {function} - 用于绑定元素的 ref 回调函数
 */
const useIntersectionAnimation = (
  delayGap = 200,
  threshold = 0.1,
  resetOnLeave = true
) => {
  const animationRefs = useRef([]);
  const observerRef = useRef(null);
  const elIndexMap = useRef(new Map()); // 元素-索引映射

  // 绑定 ref 的回调函数
  const setAnimationRef = (el, index) => {
    if (el) {
      animationRefs.current[index] = el;
      elIndexMap.current.set(el, index);
    }
  };

  useEffect(() => {
    // 初始化 Intersection Observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          const index = elIndexMap.current.get(el);
          if (entry.isIntersecting) {
            // 进入视口：延迟添加 active 类
            setTimeout(() => {
              el.classList.add("active");
            }, index * delayGap);
          } else if (resetOnLeave) {
            // 离开视口：重置状态（移除 active 类）
            el.classList.remove("active");
          }
        });
      },
      { threshold }
    );

    // 监听所有元素
    animationRefs.current.forEach((el) => {
      if (el) {
        observerRef.current.observe(el);
      }
    });

    // 组件卸载时清理监听
    return () => {
      if (observerRef.current) {
        animationRefs.current.forEach((el) => {
          if (el) {
            observerRef.current.unobserve(el);
          }
        });
      }
    };
  }, [delayGap, threshold, resetOnLeave]);

  return setAnimationRef;
};

export default useIntersectionAnimation;
